<template>
    <div class="product-slider-wrapper pb-6 xl:pb-12 mx-auto w-full relative md:min-h-[583px]">
        <button
            :id="prevId"
            class="swiper-button-prev swiper-pagination-element swiper-custom-button-prev btn border-none btn-primary absolute top-1/2 -left-6 transform -translate-y-1/2 z-40 -mt-8"
            :aria-label="t('product.slider.prev')"
        >
            <MiscIcon name="chevron-left" />
        </button>

        <div v-if="swiperReady" class="overflow-hidden relative -ml-4 w-[calc(100%+32px)] px-4">
            <Swiper
                v-if="products"
                ref="swiperCarousel"
                class="product-slider w-full pb-5 !overflow-visible md:!overflow-hidden"
                :modules="[Navigation, A11y]"
                :a11y="a11yMessages"
                :watch-slides-progress="true"
                :slides-per-view="products.length > 1 ? 1.2 : 1"
                :space-between="12"
                :loop="products.length>1"
                :auto-height="false"
                :direction="direction"
                :navigation="{
                    enabled: true,
                    nextEl: '#' + nextId,
                    prevEl: '#' + prevId,
                }"
                :breakpoints="{
                    '768': {
                        loop: products.length > 2,
                        slidesPerView: products.length > 2 ? 2.8 : products.length,
                    },
                    '1024': {
                        loop: products.length >= 4,
                        slidesPerView: products.length >= 4 ? 4 : products.length,
                    },
                    '1324': {
                        loop: products.length>5,
                        slidesPerView: products.length >= 5 ? 5 : products.length,
                    },
                }"
                role="group"
                :aria-roledescription="t('product.slider.roleCarousel')"
                :aria-label="t('product.slider.main')"
                @lock="swiperLocked = true"
                @unlock="swiperLocked = false"
            >
                <template v-for="(slide, index) in products" :key="index">
                    <SwiperSlide
                        ref="slides"
                        class="product-swiper-slide w-full md:min-h-[507px]"
                        :aria-roledescription="t('product.slider.roleSlide')"
                    >
                        <div class="relative pb-7">
                            <ProductListingCard
                                :key="slide.id"
                                :data="slide"
                                :box-layout="boxLayout"
                                :show-add-to-cart="showAddToCart"
                                @click="sendFeedbackToEpoq(slide)" />
                        </div>
                    </SwiperSlide>
                </template>
            </Swiper>
        </div>

        <button
            :id="nextId"
            class="swiper-button-next swiper-pagination-element swiper-custom-button-next btn border-none btn-primary absolute top-1/2 -right-6 transform -translate-y-1/2 z-40 -mt-8"
            :aria-label="t('product.slider.next')"
        >
            <MiscIcon name="chevron-right" />
        </button>
    </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref } from 'vue'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { A11y, Navigation } from 'swiper/modules'
import 'swiper/css'
import type { HblProduct } from '@/utils/types'

const { t } = useI18n()

const props = withDefaults(defineProps<{
    products?: HblProduct[],
    boxLayout: string,
    sliderId?: string,
    direction?: string,
    showAddToCart?: boolean
}>(), {direction: 'horizontal', sliderId: 'default', showAddToCart: false})

const slides = ref([])
const swiperCarousel = ref(null)
const swiperLocked = ref(false)
const swiperReady = ref(false)

// Swiper replaces {{index}} and {{slidesLength}} itself.
const a11yMessages = computed(() => ({
    prevSlideMessage: t('product.slider.prev'),
    nextSlideMessage: t('product.slider.next'),
    firstSlideMessage: t('product.slider.first'),
    lastSlideMessage: t('product.slider.last'),
    slideLabelMessage: t('product.slider.slideLabel', { index: '{{index}}', total: '{{slidesLength}}' }),
}))

const prevId = computed(() => `prev-${props.sliderId || 'default'}`)
const nextId = computed(() => `next-${props.sliderId || 'default'}`)

onMounted(() => {
    nextTick(() => {
        swiperReady.value = true
    })
})

const sendFeedbackToEpoq = function(slide) {
    if(slide?.feedbackUrl) {
        $fetch('/api/epoq/feedback-click',{
            method: 'post',
            body: {
                feedbackUrl: slide?.feedbackUrl
            }
        })
    }
}
</script>
